import { Dropdown, Tooltip } from 'antd';
import { MoreVertical, Plus } from 'lucide-react';
import type { AttendanceState, OfficerSummaryDto } from '@pams/shared';
import { OfficerAvatar } from '@/components/OfficerAvatar';
import type { FillControls } from '@/lib/fillPermissions';
import { cn } from '@/lib/utils';
import { presenceWord } from '@/lib/statusPresentation';
import { ATTENDANCE_WASH } from './attendanceWash';
import { FILL_COPY, refusalFor } from './copy';

/**
 * One slot, as a chip.
 *
 * ⚠️ AN EMPTY SLOT IS A BUTTON WHOSE ACCESSIBLE NAME SAYS WHERE IT IS. The chip renders a
 * `+` and nothing else, so "Add UB Men, slot 2 of 4" is the only thing a screen reader — or
 * the twelve-name budget test — has to go on.
 *
 * ⚠️ A FILLED CHIP IS NOT A BUTTON. It has no single action: `⋮` offers Swap and Remove.
 * PA_REQUIREMENTS §6.2 asks for the overflow rather than a bare `✕`, and the reason is the
 * count — twelve chips with two controls each is twenty-four tab stops with the destructive
 * one under the thumb.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended PRE-P6 §2d] THE CHIP SHOWS A NAME. THE RANK MOVED TO `title`.
 *
 *  It used to render `officer.rankShortName` beside the name as a `shrink-0` element, so the
 *  NAME truncated and the rank never did. That is backwards on this screen — the row header
 *  above already says which POST is being filled, and the one thing a chip exists to tell you
 *  is who is in the slot.
 *
 *  ⚠️ AND IT IS WHY THE `⋮` OVERLAPPED THE NEXT SLOT. `FillLines` lays the chips out in a
 *  fixed-column grid; at the old ten-column cap each cell was about 85px, while the chip's
 *  NON-SHRINKABLE content was avatar 24 + gaps 24 + rank ~30 + `⋮` 20 ≈ 98px before a single
 *  character of the name. The chip overflowed its own grid cell, and what spilled was the
 *  control at its right edge. Dropping the rank buys back ~38px; the grid cap coming down to
 *  six buys the rest.
 *
 *  ⚠️ THE RANK IS NOT LOST. It is in the chip's `title`, in the `⋮` button's accessible name,
 *  and at full size on every row of the search sheet — which is where somebody is choosing
 *  between two officers called Sen and where the disambiguation is actually needed.
 *
 *  ⚠️ [amended PRE-P6 §2e] AND THE `⋮` IS 44×44, INSIDE THE BORDER.
 *
 *  It was a 16px icon with 2px of padding — about 20×20 — in a 36px chip. It is now the full
 *  height of a 44px chip and 44px wide, flush to the right edge and INSIDE it, so it cannot
 *  overlap a neighbour whatever the grid does. The chip keeps no right padding for that
 *  reason. Only a browser can check the rectangle: `e2e/specs/layout.spec.ts` measures it.
 * ─────────────────────────────────────────────────────────────────────────────────
 *
 * ⚠️ THE CHIP CARRIES NO TONE. Red/amber/green lives on the LINE's `StatusDot` and on the
 * footer's one `StatusPill`; twelve coloured chips is the wall of badges the design language
 * forbids, and it would leave the one status that matters meaning nothing.
 */
export type SlotChipProps = {
  lineLabel: string;
  index: number;
  total: number;
  officer: OfficerSummaryDto | null;
  /** `staged` is a proposal from "copy from last time" that nobody has saved yet. */
  state: 'empty' | 'filled' | 'staged';
  active: boolean;
  /**
   * ⚠️ [C3c §7] THE OFFICER'S ATTENDANCE, PRESENT ONLY ON AN ELAPSED DUTY. Its ABSENCE is the
   * signal that the duty has not run — see `fillSlotSchema`.
   */
  attendance?: AttendanceState | undefined;
  /**
   * ⚠️ [C3c §3d] THE FIVE VERBS, NOT ONE `readOnly`. Naming and swapping close at different
   * instants, so this chip's `+` and its `Swap` are refused for different reasons at different
   * times — see `lib/fillPermissions.ts`.
   */
  controls: FillControls;
  onAdd: () => void;
  onSwap: () => void;
  onRemove: () => void;
  /**
   * ⚠️ [PRE-P6.1] THE CHIP REPORTS ITS OWN HOVER; IT DOES NOT OWN A CARD.
   *
   * `FillLines` renders ONE `OfficerHoverCard` for the whole screen — divergence #82 measured
   * what an overlay per row costs, and a fill line renders up to twelve chips. The element is
   * handed up so the card can be placed from its rectangle.
   *
   * ⚠️ FOCUS AS WELL AS POINTER. Hover is unavailable on touch and unreachable from a
   * keyboard; the `⋮` is the chip's only focusable node and focus events bubble, so the
   * handler on the chip catches it.
   */
  onHoverChange?: (element: HTMLElement | null) => void;
  /** `aria-describedby` for the `⋮`, so a screen reader gets what a pointer gets. */
  describedBy?: string | undefined;
};

export const SlotChip = ({
  lineLabel,
  index,
  total,
  officer,
  state,
  active,
  attendance,
  controls,
  onAdd,
  onSwap,
  onRemove,
  onHoverChange,
  describedBy,
}: SlotChipProps) => {
  if (state === 'empty' || !officer) {
    const button = (
      <button
        type="button"
        disabled={!controls.mayName}
        onClick={onAdd}
        aria-label={FILL_COPY.addSlot(lineLabel, index, total)}
        data-testid="slot-add"
        className={cn(
          /* ⚠️ [PRE-P6 §2e] `h-11 min-w-11` — 44px, matching the filled chip so a part-filled
             row does not step up and down, and so the `+` is a real target in its own right. */
          /* ⚠️ [UX1 §1f] `border-slot-outline`, NOT `border-border`. An empty slot is the thing this
             screen exists to find, and at the hairline alpha it was ~1.16:1 against the sheet. */
          'inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 rounded-md border border-dashed border-slot-outline px-2.5',
          /* ⚠️ HOVER GOES TO `ring`, NOT `input`. `input` is `borderStrong` at 0.13 — WEAKER
             than the new rest state — so pointing at an empty slot used to make it fainter. */
          'text-sm text-muted-foreground transition-colors hover:border-ring hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
          active && 'border-solid border-ring ring-2 ring-ring',
        )}
      >
        {/*
          ⚠️ [D4.3 §5f] AN EMPTY AVATAR, SO "8 of 12" IS A SHAPE AND NOT ONLY A NUMBER.
          A dashed ring the size of a filled chip's avatar, beside the `+`. It is what makes a
          row of twelve readable at a glance — the eye counts holes faster than it reads a
          fraction, and the fraction is still in the line's own `StatusDot`.

          `aria-hidden`: the button's `aria-label` already says "Add UB Men, slot 2 of 4", and
          a decorative circle announcing itself would say it twice.
        */}
        <span
          aria-hidden
          data-testid="slot-empty-avatar"
          className="size-6 shrink-0 rounded-full border border-dashed border-slot-outline"
        />
        <Plus className="size-3.5" aria-hidden />
      </button>
    );

    /* ⚠️ THE SENTENCE MATCHES THE REASON. A window refusal is not a capability refusal, and
       `READ_ONLY_TOOLTIP` would tell a nodal their access is view-only when it is not. */
    /* ⚠️ THE EMPTY-SLOT `+` IS A NAMING CONTROL, so it asks about NAMING — otherwise a duty
       whose swap window shut first would put a swap refusal on a button that still works. */
    const why = refusalFor(controls, 'name');
    return why === undefined ? button : <Tooltip title={why}>{button}</Tooltip>;
  }

  /*
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3c §7] THE ATTENDANCE WASH — TWO WEIGHTS OF ONE HUE, AND THE ORDINARY CASE HAS NONE.
   *
   *  ⚠️ THIS IS THE FIRST COLOUR IN THIS APP THAT DOES NOT MEAN FULFILMENT, and it must not be
   *  confusable with the colour that does. Red, amber and green are named-versus-required
   *  everywhere else; `attendance` is a DESATURATED SLATE for exactly that reason — "not
   *  saturated" reads as "not a status" before anybody has learned what it means.
   *
   *  ⚠️ AN OPACITY MODIFIER OVER A TONE TOKEN, WHICH IS NOT WHAT §8 BANS. That rule — "there are
   *  no tint TOKENS and none may be added" — forbids a PRE-MIXED wash of an existing tone,
   *  because the modifier already expresses it and follows a `ThemeScope` for free. This is the
   *  modifier, over a tone that had no token. And it is a CLASS rather than an inline
   *  `color-mix`, because jsdom's cssstyle silently DROPS that declaration ([D4.5]) — a class is
   *  the only form a unit test can read.
   *
   *  ⚠️ [amended C3c.1] AND IT IS AN `after:` OVERLAY RATHER THAN A SECOND `bg-*`, BECAUSE
   *  `twMerge` DELETES THE CHIP'S OWN `bg-card` OTHERWISE. See `ATTENDANCE_WASH` above — that
   *  is the defect this pass found, and dark mode is where it made the wash disappear entirely.
   *
   *  ⚠️ TWO WEIGHTS RATHER THAN TWO HUES, because `ATTENDANCE_STATES` is an ORDINAL scale
   *  (`NOT_STARTED → STARTED → REACHED → STOOD_DOWN`) and "how far up did this person get" is
   *  what an operator is reading. Two hues would say two unrelated things.
   *
   *  ⚠️ AND `REACHED`/`STOOD_DOWN` CARRY NO WASH AT ALL. Turning up is the expected outcome;
   *  washing every row on a normal duty is noise that hides the two exceptions — `ForceGrid`'s
   *  `empty` argument, and `SlotChip`'s own "twelve coloured chips is the wall of badges the
   *  design language forbids".
   *
   *  ⚠️ "DID NOT START" AND "MARKED ABSENT" ARE ONE VALUE, MEASURED. `attendanceOf` answers
   *  `ABSENT` both for an explicit event and — via `dutyEnded` — for a subject with nothing
   *  recorded (FR-5.4.6's derived absence), so `NOT_STARTED` is UNREACHABLE once a duty has
   *  elapsed. Telling them apart would need a seventh `ATTENDANCE_STATES` member, which
   *  [LIFECYCLE]'s one-vocabulary rule refuses.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  /*
   * ⚠️ [amended C5c §5a] U6 — THE WORD IS THE ROSTER'S OWN, `Present` / `Absent`, through the shared
   * `presenceOf`. `true` is fixed: the field is present only on an ELAPSED duty, which has started.
   * And one wash, ABSENT — a Start Duty reads Present, so washing it as an exception is gone.
   *
   * ⚠️ THE DASHBOARD TILE IS LEFT ALONE, AND DELIBERATELY. It answers "how many reached the point"
   * under the word Reached, folding a Start Duty with no Reach into "not reached" — a narrower
   * question the client did not ask to change. Recorded rather than harmonised.
   */
  const wash = attendance === undefined ? undefined : ATTENDANCE_WASH[attendance];
  const word = attendance === undefined ? undefined : (presenceWord(attendance, true) ?? undefined);

  const items = [
    ...(controls.maySwap ? [{ key: 'swap', label: FILL_COPY.swap, onClick: onSwap }] : []),
    ...(controls.mayRemove ? [{ key: 'remove', label: FILL_COPY.remove, danger: true, onClick: onRemove }] : []),
  ];

  return (
    <span
      data-testid="slot-chip"
      data-state={state}
      /* ⚠️ ON THE CHIP, NOT ON THE `⋮`: the card describes the officer, and the whole chip is
         what an operator points at. `onFocus`/`onBlur` are React's bubbling focusin/focusout,
         so focusing the `⋮` inside opens it too. */
      onMouseEnter={(event) => onHoverChange?.(event.currentTarget)}
      onMouseLeave={() => onHoverChange?.(null)}
      onFocus={(event) => onHoverChange?.(event.currentTarget)}
      onBlur={() => onHoverChange?.(null)}
      /*
        ⚠️ NO RIGHT PADDING. The `⋮` is 44px wide and sits flush inside the border, which is
        what stops it spilling into the neighbouring grid cell.

        ⚠️ `min-h-11`, NOT `h-11`. Preflight makes every box `border-box`, so `h-11` would be
        44px INCLUDING the 1px borders and a `size-11` child would overflow it by two. The
        minimum lets the content box be a true 44 — which is what the browser assertion
        measures, and it is the whole of §2e.
      */
      /* ⚠️ [C3c §7] AND `data-attendance` IS WHAT A TEST READS. jsdom resolves no cascade, so the
         WASH is only ever a class string there; the attribute is the assertable fact, and it
         also keeps the browser sweep able to find these rows by state. */
      {...(attendance === undefined ? {} : { 'data-attendance': attendance })}
      className={cn(
        'relative inline-flex min-h-11 max-w-full items-center gap-2 rounded-md border py-0 pl-2 pr-0 text-sm',
        state === 'staged' ? 'border-dashed border-info' : 'border-border bg-card',
        /*
         * ⚠️ `isolate` IS LOAD-BEARING AND IS NOT DECORATION. It makes the chip a stacking
         * context, which is what bounds the overlay's `-z-10` to this element — without it the
         * negative index resolves against the nearest ancestor stacking context and the wash
         * paints BEHIND the page's own background, i.e. nowhere.
         */
        wash === undefined ? undefined : 'isolate',
        wash,
      )}
    >
      <OfficerAvatar name={officer.name} src={officer.photoUrl ?? undefined} className="size-6 shrink-0" />
      {/* ⚠️ [PRE-P6 §2d] THE NAME, AND ONLY THE NAME.
          ⚠️ [PRE-P6.1] AND NO `title` ANY MORE. It carried the rank while the chip was the only
          place to see it; `OfficerHoverCard` now shows the rank, the phone and the lineage on
          the same hover, and a native tooltip racing a real one is two answers to one gesture —
          the browser's arrives late, in its own style, and cannot be dismissed. */}
      <span className="min-w-0 truncate">{officer.name}</span>
      {/*
        ⚠️ [amended C3c.1] A WORD AS WELL AS A WASH — CLAUDE.md §8 [C3c §7] REQUIRES ONE, AND §7
        SHIPPED WITH NONE AT ALL. The chip carried `data-attendance` and nothing else, which is a
        TEST HOOK and reaches no assistive technology, so an elapsed duty conveyed attendance to a
        screen reader not at all and to a colour-blind operator through two alphas of one hue.

        ⚠️ `sr-only`, AND DELIBERATELY **NOT** A NATIVE `title`. [PRE-P6.1] removed this chip's
        `title` on the stated ground that a browser tooltip racing `OfficerHoverCard` is two
        answers to one gesture — the native one arrives late, in its own style, and cannot be
        dismissed. Reintroducing it for this word would re-open exactly that. The VISIBLE channel
        is the hover card, which opens on hover AND on focus and carries the same line.
      */}
      {word === undefined ? null : <span className="sr-only">{word}</span>}
      {/*
        ⚠️ [C3c §3d] SWAP AND REMOVE ARE GATED SEPARATELY NOW, AND THE SPLIT IS BY **OMISSION**.

        They used to sit behind one `disabled` on the Dropdown, which cannot express "naming has
        closed but swapping has not". And they may NOT be split with `disabled` on the menu items:
        [UX-FINAL] measured that rc-menu CACHES an item's `disabled` from the overlay's FIRST
        render (divergence #53), so an item disabled later stays disabled for ever and one enabled
        later never becomes clickable. The list is built per render instead.

        ⚠️ AND A MENU THAT WOULD OPEN EMPTY IS NOT RENDERED AT ALL ([C3b §4]). The `⋮` stays as a
        disabled button rather than vanishing, because it is also this chip's only focusable node
        and the hover card is reached through it.
      */}
      <Dropdown disabled={items.length === 0} trigger={['click']} menu={{ items }}>
        <button
          type="button"
          disabled={items.length === 0}
          data-testid="slot-chip-actions"
          /* ⚠️ THE RANK RIDES ALONG HERE TOO, so the one control on the chip still names the
             officer unambiguously to a screen reader. */
          aria-label={FILL_COPY.chipActions(officer.name, officer.rankShortName, lineLabel, index)}
          aria-describedby={describedBy}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50"
        >
          <MoreVertical className="size-4" aria-hidden />
        </button>
      </Dropdown>
    </span>
  );
};
